/**
 * La GÉOMÉTRIE du radar : sommets d'un polygone régulier, anneaux de grille,
 * découpe des intitulés.
 *
 * Fichier séparé du composant : un `.jsx` qui exporte autre chose qu'un
 * composant casse le Fast Refresh de Vite, et l'écran part blanc sans erreur.
 */

export const WIDTH = 600
export const HEIGHT = 390
export const CX = 300
export const CY = 195
export const R = 126
export const LABEL_GAP = 30
export const WRAP_AT = 14

/** Le sommet d'index `i` sur `n` axes, à la distance `r` du centre. */
export const vertex = (i, n, r) => {
  const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n
  return [CX + Math.cos(angle) * r, CY + Math.sin(angle) * r]
}

export const polygon = (n, r) =>
  Array.from({ length: n }, (_, i) => vertex(i, n, r).map((v) => v.toFixed(1)).join(',')).join(' ')

/** Découpe un intitulé en lignes d'environ `WRAP_AT` caractères. */
export const wrapLabel = (text, at = WRAP_AT) => {
  const lines = []
  let current = ''
  String(text ?? '')
    .split(' ')
    .forEach((word) => {
      if (!current) current = word
      else if (`${current} ${word}`.length <= at) current += ` ${word}`
      else { lines.push(current); current = word }
    })
  if (current) lines.push(current)
  return lines
}

export const clampRatio = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return 0
  return Math.min(Math.max(n, 0), 1)
}

/**
 * Le secteur (part de tarte) centré sur l'axe `i` : la zone de clic d'un axe lié.
 * Demi-ouverture = un demi-pas entre deux axes, donc les secteurs pavent le
 * disque sans se recouvrir.
 */
export const sectorPath = (i, n, r) => {
  const step = (2 * Math.PI) / n
  const mid = -Math.PI / 2 + i * step
  const a1 = mid - step / 2
  const a2 = mid + step / 2
  const p = (a) => `${(CX + Math.cos(a) * r).toFixed(1)},${(CY + Math.sin(a) * r).toFixed(1)}`
  return `M${CX},${CY} L${p(a1)} A${r},${r} 0 0 1 ${p(a2)} Z`
}

/**
 * Adresse du lien d'un axe : jetons `:nom` du gabarit remplacés par les valeurs
 * (même convention que `PathLink`, valeurs encodées). Un jeton sans valeur
 * rend `null` — pas de lien plutôt qu'un lien cassé.
 */
export const axisHref = (template, values) => {
  if (typeof template !== 'string' || template === '') return null
  let missing = false
  const href = template.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_m, name) => {
    const v = values?.[name]
    if (v == null || v === '') { missing = true; return '' }
    return encodeURIComponent(String(v))
  })
  return missing ? null : href
}

/**
 * Intitulé complet d'un axe lié : « Sécurité : 87, bon. Ouvrir le rapport du
 * domaine ». Le ton est dit EN TOUTES LETTRES (`toneLabel`), jamais porté par
 * la seule couleur.
 */
export const axisLinkLabel = (axis, hint) => {
  const head = [axis?.label, axis?.value != null && axis.value !== '' ? String(axis.value) : null]
    .filter(Boolean)
    .join(' : ')
  const withTone = axis?.toneLabel ? `${head}, ${String(axis.toneLabel).toLowerCase()}` : head
  return hint ? `${withTone}. ${hint}` : withTone
}
